/**
 * Minimal ⌘K command palette: route jumps + a couple of UI actions.
 * Opens on Cmd/Ctrl+K, filters with fuzzy-ish substring match, arrow keys +
 * Enter to run.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type Command = {
  id: string
  label: string
  hint: string
  run: () => void
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const commands = useMemo<Command[]>(
    () => [
      { id: 'go-overview', label: 'go: overview', hint: '/', run: () => navigate('/') },
      { id: 'go-portfolio', label: 'go: portfolio', hint: '/portfolio', run: () => navigate('/portfolio') },
      { id: 'go-signals', label: 'go: signals', hint: '/signals', run: () => navigate('/signals') },
      { id: 'go-trades', label: 'go: trades', hint: '/trades', run: () => navigate('/trades') },
      { id: 'go-methodology', label: 'go: methodology', hint: '/methodology', run: () => navigate('/methodology') },
      { id: 'go-legal', label: 'go: legal', hint: '/legal', run: () => navigate('/legal') },
      { id: 'go-posts', label: 'go: x posts', hint: '/posts', run: () => navigate('/posts') },
      { id: 'act-reload', label: 'action: reload data', hint: 'full page reload', run: () => window.location.reload() },
      { id: 'act-top', label: 'action: scroll to top', hint: '', run: () => window.scrollTo({ top: 0 }) },
    ],
    [navigate],
  )

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q),
    )
  }, [commands, query])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
        setQuery('')
        setCursor(0)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, matches.length - 1)))
  }, [matches.length])

  if (!open) return null

  const runAt = (i: number) => {
    const cmd = matches[i]
    if (!cmd) return
    setOpen(false)
    cmd.run()
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-black/70 pt-[15vh]"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        className="panel panel-glow w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="command palette"
      >
        <input
          ref={inputRef}
          className="input border-0 border-b border-lime-soft"
          placeholder="type a command…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setCursor(0)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setCursor((c) => Math.min(c + 1, matches.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setCursor((c) => Math.max(c - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              runAt(cursor)
            }
          }}
        />
        <ul className="max-h-72 overflow-y-auto">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-crt-dim">no matches</li>
          ) : (
            matches.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                    i === cursor
                      ? 'bg-crt-lime/10 text-crt-lime'
                      : 'text-crt-gray hover:text-crt-lime'
                  }`}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => runAt(i)}
                >
                  <span>{c.label}</span>
                  <span className="text-[10px] text-crt-dim">{c.hint}</span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="border-t border-crt-dim/40 px-3 py-1.5 text-[10px] text-crt-dim">
          <span className="kbd">↑↓</span> navigate <span className="kbd">↵</span>{' '}
          run <span className="kbd">esc</span> close
        </div>
      </div>
    </div>
  )
}
